/* eslint-disable react-hooks/exhaustive-deps */
/**
 * Add / Edit dialog for "College Course - Custom Data" (grade_no 13) in GPA Forecaster v1.
 *
 * Same layout and rules as the Grade 9th-12th dialog (formCreateGpaForecastModal), with the
 * College Courses page fields instead of the high school ones:
 *  - Course Name (free text, required) and College Course Subject (autocomplete from
 *    collegeCourseSubjectOption). The subject is NOT stored: gpa_forecast_detail has no column
 *    for it. Picking one only prefills A-G Subject Area with its `agArea`, like Subject/Discipline
 *    in the grade dialog. The student can still change A-G.
 *  - Term is always "Final" for college blocks; Grade uses the block's scale (A-F when empty).
 *  - Validation is the shared validateCustomCourse().
 */
import * as React from 'react';
import { MenuItem, ListItemText, OutlinedInput } from '@mui/material';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useFormik } from 'formik';
import { Row, Col, Form } from 'react-bootstrap';
import TextInput from 'components/input/textInput';
import SelectInput from 'components/input/SelectInput';
import SelectSearchInput from 'components/input/SelectSearchInput';
import { CssSelect } from 'components/helper/helperInut';
import { subjectAreaOptions } from 'screen/course/courseConstant';
import {
  collegeCourseSubjectOption,
  getCollegeSubjectAg,
} from 'screen/course/collegeCourse/components/collegeCourseContant';
import { genGradeOptions, genTermOptions, validateCustomCourse } from './formCreateGpaForecastModal';

const COLLEGE_GRADE_NO = 13;
// College sync stores grading_scale '' on new blocks; college grades are letter grades.
const collegeScale = (scale: any) => (`${scale ?? ''}`.trim() ? `${scale}` : 'A-F');
const asYears = (v: any): string[] =>
  (Array.isArray(v) ? v : [v]).map((x: any) => `${x ?? ''}`.trim()).filter((x: string) => !!x);

type Props = {
  open: boolean;
  options: any[];                 // school blocks of grade 13 (getSchoolOptionsByCourse)
  handleSave: (values: any) => void;
  onClose: () => void;
  mode?: 'add' | 'edit';
  editRow?: any;                  // gpa_forecast_detail row when mode = 'edit'
};

const FormCollegeCourseModal = ({ open, options, handleSave, onClose, mode = 'add', editRow = null }: Props) => {
  const isEdit = mode === 'edit' && !!editRow;
  const hasSchoolOptions = !!(options && options.length > 0);
  const subjectRef = React.useRef<any>(null);

  const [school, setSchool] = React.useState<any>(null);
  const [yearOptions, setYearOptions] = React.useState<string[]>([]);
  const [termOptions, setTermOptions] = React.useState<any[]>(genTermOptions('', COLLEGE_GRADE_NO));
  const [gradeOptions, setGradeOptions] = React.useState<any[]>(genGradeOptions('A-F'));
  const [errors, setErrors] = React.useState<Record<string, string>>({});

  const formik = useFormik({
    initialValues: {
      gpa_forecast_id: undefined as any,
      school_name: '',
      grading_scale: 'A-F',
      schedule: '',
      school_year: '',
      school_code: '',
      course: '',
      college_course_subject: '',   // UI only, used to prefill subject_area
      subject_area: '',
      term: 'Final',
      grade: '',
      credit: 1 as any,
      is_weight_point: true,        // college courses are weighted (see college GPA sync)
      unweighted_point: '' as any,
      weighted_point: '' as any,
    },
    onSubmit: () => {},
  });

  // Apply a school block to the form (Add: user pick; Edit: the row's own block).
  const applySchool = (block: any, base: any) => {
    const scale = collegeScale(block?.grading_scale);
    const years = asYears(block?.school_year);
    const terms = genTermOptions(block?.schedule ?? '', COLLEGE_GRADE_NO);
    const grades = genGradeOptions(scale);
    setSchool(block);
    setYearOptions(years);
    setTermOptions(terms);
    setGradeOptions(grades);
    return {
      ...base,
      gpa_forecast_id: block?.gpa_forecast_id,
      school_name: block?.school_name ?? '',
      grading_scale: scale,
      schedule: block?.schedule ?? '',
      school_code: block?.school_code ?? '',
      school_year: years.length > 0 ? years[0] : '',
      term: terms.length > 0 ? terms[0].value : 'Final',
      grade: base.grade || (grades.length > 0 ? grades[0].value : ''),
    };
  };

  React.useEffect(() => {
    if (!open) {
      formik.resetForm();
      setErrors({});
      return;
    }
    if (!hasSchoolOptions) return;
    const block = isEdit
      ? (options.find((o: any) => o.gpa_forecast_id === editRow.gpa_forecast_id) ?? options[0])
      : options[0];
    const base = isEdit
      ? {
          ...formik.initialValues,
          course: editRow.course ?? '',
          subject_area: editRow.ag_subject_area ?? '',
          grade: editRow.grade ?? '',
          credit: editRow.credit ?? '',
          is_weight_point: editRow.is_weight_point ?? true,
          unweighted_point: editRow.unweighted_point ?? '',
          weighted_point: editRow.weighted_point ?? '',
        }
      : { ...formik.initialValues };
    const next = applySchool(block, base);
    if (isEdit && editRow.term) next.term = editRow.term;
    formik.setValues(next);
    setErrors({});
  }, [open, options, editRow]);

  const handleSaveClick = () => {
    const errs = validateCustomCourse(formik.values, termOptions);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    handleSave(formik.values);
    onClose();
  };

  const subjectValue = formik.values.college_course_subject
    ? {
        label: formik.values.college_course_subject,
        value: formik.values.college_course_subject,
        code: formik.values.college_course_subject,
        id: collegeCourseSubjectOption.findIndex((o) => o.value === formik.values.college_course_subject),
      }
    : undefined;

  if (!open) return null;

  return (
    <div className="custom-accordion">
      <div className="custom-accordion-item">
        <h2 className="custom-accordion-header">
          {isEdit ? 'Edit College Course Information' : 'New College Course Information'}
        </h2>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={(theme) => ({ position: 'absolute', right: 8, top: 8, color: theme.palette.grey[500] })}
        >
          <CloseIcon />
        </IconButton>

        <div className="custom-accordion-body">
          {!hasSchoolOptions ? (
            // No college block yet: a custom course must attach to an existing gpa_forecast row.
            <div className="pt-2 pb-2" style={{ lineHeight: 1.6 }}>
              There is no college set up yet. Add the college and its courses in Courses &amp; Grades
              (College Courses) first, then click Refresh Data here to add individual courses.
            </div>
          ) : (
            <div className="pt-2">
              <Row className="pb-2">
                <Form.Group as={Col} md={6} className="ps-1 pe-2 py-2">
                  <Form.Label className="select-label label-14px">School Name</Form.Label>
                  <div style={{ backgroundColor: 'white', borderRadius: '0.5rem' }}>
                    <CssSelect
                      name="select_college"
                      // The row belongs to its college block; moving it is not an edit.
                      disabled={isEdit}
                      value={school}
                      onChange={(e: any) => {
                        const block = e.target.value;
                        if (block) formik.setValues(applySchool(block, formik.values));
                      }}
                      input={<OutlinedInput label="Tag" />}
                      renderValue={(o: any) => o?.school_name}
                    >
                      {options.map((o: any) => (
                        <MenuItem key={o.gpa_forecast_id} value={o}>
                          <ListItemText primary={o.school_name} />
                        </MenuItem>
                      ))}
                    </CssSelect>
                  </div>
                </Form.Group>
                <SelectInput
                  id="collegeSchoolYear"
                  form={formik}
                  option={yearOptions}
                  mdCol="6"
                  label="School Year"
                  values={formik.values.school_year}
                  name="school_year"
                  onChange={formik.handleChange}
                />
                <TextInput
                  id="collegeCourseName"
                  mdCol="12"
                  label="Course Name*"
                  countText={50}
                  name="course"
                  values={formik.values.course}
                  onChange={formik.handleChange}
                />
                <SelectSearchInput
                  mdCol="12"
                  form={formik}
                  option={collegeCourseSubjectOption.map((o, index) => ({
                    label: o.value,
                    value: o.value,
                    code: o.value,
                    id: index,
                  }))}
                  label="College Course Subject"
                  values={subjectValue}
                  name="college_course_subject"
                  onChangeItem={(e: any) => {
                    formik.setFieldValue('college_course_subject', e?.value ?? '');
                    // Prefill A-G from the subject map; the student can still change it.
                    const mapped = getCollegeSubjectAg(e?.value);
                    if (mapped && mapped.agArea) formik.setFieldValue('subject_area', mapped.agArea);
                  }}
                  onSearch={() => {}}
                  inputRef={subjectRef}
                />
                <SelectInput
                  form={formik}
                  option={subjectAreaOptions}
                  label="A-G Subject Area*"
                  values={formik.values.subject_area}
                  name="subject_area"
                  onChange={formik.handleChange}
                />
              </Row>

              <Row className="py-2">
                <TextInput mdCol={3} label="Schedule" values={formik.values.schedule} name="schedule" readOnly={true} />
                <TextInput mdCol={3} label="Grading Scale" values={formik.values.grading_scale} name="grading_scale" readOnly={true} />
                <SelectInput mdCol={3} option={termOptions} label="Term" values={formik.values.term} name="term" onChange={formik.handleChange} />
                <SelectInput mdCol={3} option={gradeOptions} label="Grade" values={formik.values.grade} name="grade" onChange={formik.handleChange} />
              </Row>
              <Row className="py-2">
                <TextInput type="number" decimalStep={0.1} mdCol={4} label="Unweighted point"
                  values={formik.values.unweighted_point} name="unweighted_point" onChange={formik.handleChange} />
                <TextInput type="number" decimalStep={0.1} mdCol={4} label="Weighted point"
                  values={formik.values.weighted_point} name="weighted_point" onChange={formik.handleChange} />
                <TextInput type="number" decimalStep={0.1} mdCol={4} label="Credits"
                  values={formik.values.credit} name="credit" onChange={formik.handleChange} />
              </Row>
            </div>
          )}
          {Object.keys(errors).length > 0 && (
            // Result of the last Save click; nothing is saved while this list is shown.
            <ul className="mt-2 mb-0 ps-4" style={{ color: '#d32f2f', fontSize: '14px' }}>
              {Object.entries(errors).map(([k, msg]) => (
                <li key={k}>{msg}</li>
              ))}
            </ul>
          )}
        </div>
        <div className="custom-accordion-footer d-flex justify-content-end">
          <Button className="btn-custom" variant="outlined" style={{ marginRight: '1em' }} onClick={onClose}>
            Cancel
          </Button>
          <Button className="btn-custom" variant="contained" style={{ marginRight: '1em' }}
            disabled={!hasSchoolOptions} onClick={handleSaveClick}>
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FormCollegeCourseModal;
